import { useTranslation } from "react-i18next";

import { useToastStore } from "~/components/Shared/Toast/toastStore";
import { useDeleteFirmwareVersion } from "~/hooks/useFirmwares";
import { Button, Modal } from "~/ui";

interface DeleteFirmwareVersionModalProps {
  isOpen: boolean;
  onClose: () => void;
  version?: { id: number; version: string };
  firmwareId: number;
}

export function DeleteFirmwareVersionModal({
  isOpen,
  onClose,
  version,
  firmwareId,
}: DeleteFirmwareVersionModalProps) {
  const { t } = useTranslation();
  const { pushToast } = useToastStore();
  const { mutate: deleteVersion, isPending } = useDeleteFirmwareVersion();

  const handleDelete = () => {
    if (version) {
      deleteVersion(
        { firmwareId, firmwareVersionId: version.id },
        {
          onSuccess: () => {
            onClose();
            void pushToast({
              type: "success",
              title: t("general.success", "Success"),
              message: t(
                "firmware.delete_success",
                "Firmware version deleted successfully",
              ),
            });
          },
          onError: (error: unknown) => {
            const errorResponse = error as {
              response?: { data?: { error?: { message?: string } } };
            };
            const errorMessage =
              errorResponse?.response?.data?.error?.message ??
              t("general.error_occurred", "An error occurred");
            void pushToast({
              type: "error",
              title: t("general.error", "Error"),
              message: errorMessage,
            });
          },
        },
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("firmware.delete_version")}
    >
      <div>{t("firmware.delete_confirm", { version: version?.version })}</div>
      <div className="mt-4 flex gap-2">
        <Button
          text={t("general.cancel")}
          variant="outline"
          onClick={onClose}
          disabled={isPending}
          className="flex grow"
        />
        <Button
          text={t("general.delete")}
          variant="filled"
          onClick={handleDelete}
          disabled={isPending}
          className="flex grow"
        />
      </div>
    </Modal>
  );
}
